import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { TypeBadge } from '@/components/ui/type-badge';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { toast } from 'sonner';
import { Loader2, Plus, Building2, Pencil, Trash2, Eye } from 'lucide-react';
import { format } from 'date-fns';
import { DocumentUpload } from '@/components/opportunities/DocumentUpload';

interface Opportunity {
  id: string;
  title: string;
  description: string;
  type: string;
  sector: string;
  status: string;
  deadline: string | null;
  published_at: string | null;
  created_at: string;
  document_url: string | null;
}

const STATUS_STYLES: Record<string, string> = {
  draft: 'bg-secondary text-secondary-foreground',
  open: 'bg-green-100 text-green-800',
  closed: 'bg-destructive/10 text-destructive',
};

export default function MyOpportunities() {
  const { t } = useTranslation();
  const { user, role, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [editOpp, setEditOpp] = useState<Opportunity | null>(null);
  const [deleteOpp, setDeleteOpp] = useState<Opportunity | null>(null);
  const [editForm, setEditForm] = useState({ title: '', description: '', deadline: '', status: '', document_url: '' as string | null });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!authLoading && (!user || role !== 'company')) {
      navigate('/auth');
      return;
    }
    if (user) fetchData();
  }, [user, role, authLoading]);

  const fetchData = async () => {
    if (!user) return;
    const { data: company } = await supabase
      .from('companies')
      .select('id')
      .eq('user_id', user.id)
      .single();

    if (!company) { setLoading(false); return; }
    setCompanyId(company.id);

    const { data } = await supabase
      .from('opportunities')
      .select('*')
      .eq('company_id', company.id)
      .order('created_at', { ascending: false });

    setOpportunities((data as Opportunity[]) || []);
    setLoading(false);
  };

  const filtered = statusFilter === 'all'
    ? opportunities
    : opportunities.filter(o => o.status === statusFilter);

  const openEdit = (o: Opportunity) => {
    setEditOpp(o);
    setEditForm({
      title: o.title,
      description: o.description,
      deadline: o.deadline || '',
      status: o.status === 'draft' ? 'open' : o.status,
      document_url: o.document_url,
    });
  };

  const handleSave = async () => {
    if (!editOpp) return;
    setSaving(true);
    const { error } = await supabase.from('opportunities').update({
      title: editForm.title,
      description: editForm.description,
      deadline: editForm.deadline || null,
      status: editForm.status as 'open' | 'closed',
      document_url: editForm.document_url,
    }).eq('id', editOpp.id);
    setSaving(false);
    if (error) { toast.error(error.message); return; }
    toast.success('Opportunity updated');
    setEditOpp(null);
    fetchData();
  };

  const handleDelete = async () => {
    if (!deleteOpp) return;
    const { error } = await supabase.from('opportunities').delete().eq('id', deleteOpp.id);
    if (error) { toast.error(error.message); return; }
    toast.success('Opportunity deleted');
    setDeleteOpp(null);
    fetchData();
  };

  if (authLoading || loading) {
    return (
      <Layout>
        <div className="container py-8">
          <div className="space-y-3">
            {[1,2,3].map(i => <Skeleton key={i} className="h-16 w-full" />)}
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container py-8">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <h1 className="text-3xl font-bold">{t('company.dashboard.myOpportunities')}</h1>
          <Button asChild>
            <Link to="/opportunities/create">
              <Plus className="mr-1 h-4 w-4" />
              {t('company.dashboard.postOpportunity')}
            </Link>
          </Button>
        </div>

        {opportunities.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center py-12 text-center">
              <Building2 className="mb-4 h-12 w-12 text-muted-foreground" />
              <p className="mb-4 text-muted-foreground">No opportunities yet.</p>
              <Button asChild>
                <Link to="/opportunities/create">Post your first opportunity</Link>
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Tabs value={statusFilter} onValueChange={setStatusFilter}>
            <TabsList>
              <TabsTrigger value="all">All</TabsTrigger>
              <TabsTrigger value="draft">Draft</TabsTrigger>
              <TabsTrigger value="open">Open</TabsTrigger>
              <TabsTrigger value="closed">Closed</TabsTrigger>
            </TabsList>
            <TabsContent value={statusFilter} className="mt-4">
              {filtered.length === 0 ? (
                <p className="py-8 text-center text-muted-foreground">No opportunities with this status.</p>
              ) : (
                <>
                  {/* Desktop Table */}
                  <div className="hidden md:block">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>{t('opportunities.create.titleLabel')}</TableHead>
                          <TableHead>{t('opportunities.filters.type')}</TableHead>
                          <TableHead>{t('opportunities.filters.sector')}</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>{t('opportunities.detail.deadline')}</TableHead>
                          <TableHead>Published</TableHead>
                          <TableHead>{t('common.edit')}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filtered.map(o => (
                          <TableRow key={o.id}>
                            <TableCell className="font-medium">{o.title}</TableCell>
                            <TableCell><TypeBadge type={o.type as any} /></TableCell>
                            <TableCell>{t(`sectors.${o.sector}`)}</TableCell>
                            <TableCell>
                              <Badge variant="outline" className={STATUS_STYLES[o.status] || ''}>{o.status}</Badge>
                            </TableCell>
                            <TableCell>{o.deadline ? format(new Date(o.deadline), 'MMM d, yyyy') : '—'}</TableCell>
                            <TableCell>
                              {o.published_at
                                ? format(new Date(o.published_at), 'MMM d, yyyy')
                                : <span className="text-muted-foreground">Not published</span>
                              }
                            </TableCell>
                            <TableCell>
                              <div className="flex gap-1">
                                <Button size="icon" variant="ghost" onClick={() => navigate(`/opportunities/${o.id}`)}><Eye className="h-4 w-4" /></Button>
                                <Button size="icon" variant="ghost" onClick={() => openEdit(o)}><Pencil className="h-4 w-4" /></Button>
                                {o.status === 'open' ? (
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <span><Button size="icon" variant="ghost" disabled className="text-destructive"><Trash2 className="h-4 w-4" /></Button></span>
                                    </TooltipTrigger>
                                    <TooltipContent>Close the opportunity first</TooltipContent>
                                  </Tooltip>
                                ) : (
                                  <Button size="icon" variant="ghost" className="text-destructive" onClick={() => setDeleteOpp(o)}><Trash2 className="h-4 w-4" /></Button>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>

                  {/* Mobile Cards */}
                  <div className="space-y-3 md:hidden">
                    {filtered.map(o => (
                      <Card key={o.id}>
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between">
                            <div>
                              <h3 className="font-semibold">{o.title}</h3>
                              <div className="mt-1 flex flex-wrap gap-2">
                                <TypeBadge type={o.type as any} />
                                <Badge variant="outline" className={STATUS_STYLES[o.status] || ''}>{o.status}</Badge>
                              </div>
                            </div>
                          </div>
                          <div className="mt-3 flex gap-2">
                            <Button size="sm" variant="outline" onClick={() => navigate(`/opportunities/${o.id}`)}>{t('common.view')}</Button>
                            <Button size="sm" variant="outline" onClick={() => openEdit(o)}>{t('common.edit')}</Button>
                            {o.status !== 'open' && (
                              <Button size="sm" variant="outline" className="text-destructive" onClick={() => setDeleteOpp(o)}>{t('common.delete')}</Button>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </>
              )}
            </TabsContent>
          </Tabs>
        )}

        {/* Edit Dialog */}
        <Dialog open={!!editOpp} onOpenChange={() => setEditOpp(null)}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>{t('common.edit')}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Title</Label>
                <Input value={editForm.title} onChange={e => setEditForm(f => ({ ...f, title: e.target.value }))} />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea value={editForm.description} onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))} rows={4} />
              </div>
              <div>
                <Label>Deadline</Label>
                <Input type="date" value={editForm.deadline} onChange={e => setEditForm(f => ({ ...f, deadline: e.target.value }))} />
              </div>
              {editOpp && (editOpp.type === 'tender' || editOpp.type === 'jv') && (
                <DocumentUpload
                  label="TOR / RFP Document"
                  description="Upload or replace the terms of reference document."
                  folder="opportunity-documents"
                  value={editForm.document_url}
                  onChange={(url) => setEditForm(f => ({ ...f, document_url: url }))}
                />
              )}
              <div>
                <Label>Status</Label>
                <Select value={editForm.status} onValueChange={v => setEditForm(f => ({ ...f, status: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="open">Open</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditOpp(null)}>{t('common.cancel')}</Button>
              <Button onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : t('common.save')}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirm */}
        <AlertDialog open={!!deleteOpp} onOpenChange={() => setDeleteOpp(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('common.delete')}</AlertDialogTitle>
              <AlertDialogDescription>{t('admin.confirmDelete')}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">{t('common.delete')}</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
}
